/* Dermal Diagnosis questionnaire — multi-page walker.
   Data-driven from form-spec.json (pages keyed by native jfPage). Renders ONE
   page at a time, accumulates answers in a single global { qid -> value } map,
   and after every page asks the server which pages this respondent should see
   (POST /evaluate → ordered visible jfPage list). Branching lives on the server
   because page conditions test engine-computed fields (aging-word, rosacea%,
   gender, …) that never ship to the storefront.

   Flow:  boot → /evaluate({}) → render first visible page
          Continue → capture (live) → /evaluate(answers) → next visible jfPage
          Back     → previous visible jfPage (values restored)
          last visible page → /score(answers) → result code. */
(function () {
  "use strict";

  // ---- config ----------------------------------------------------------------
  // One configurable API base. Empty = same-origin (the dev server mounts /score,
  // /evaluate, /form-spec.json and /baseline). In production the storefront sets
  // window.DD_CONTENT_API to the deployed Worker, which serves all four from one
  // origin. ?api= overrides for testing; ?score=/?evaluate=/?spec= still win.
  var qs = new URLSearchParams(location.search);
  var API = (qs.get("api") || window.DD_CONTENT_API || "").replace(/\/+$/, "");
  var SCORE_URL = qs.get("score") || (API + "/score");
  var EVALUATE_URL = qs.get("evaluate") || (API + "/evaluate");
  var SPEC_URL = qs.get("spec") || (API + "/form-spec.json");
  var BASELINE_URL = API + "/baseline";
  // Where to send the respondent once scoring succeeds. The diagnosis page
  // (app.js) reads the profile from URL params, so we redirect there carrying
  // them. Configurable via the Shopify section setting (window.DD_RESULTS_URL);
  // ?results= overrides for testing. Default matches the storefront page handle.
  /* The Shopify page that renders a diagnosis. `/pages/your-results` never
     existed — the page on the store is `your-diagnosis` — so every completed
     assessment was redirecting to a 404. */
  var RESULTS_URL = qs.get("results") || window.DD_RESULTS_URL || "/pages/your-diagnosis";
  // Dev-only affordances (sample autofill, which calls /baseline) appear only on a
  // local host — never on the storefront.
  var IS_DEV = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$/.test(location.hostname);
  /* Ask one question at a time: within a page, a question appears only once the
     one before it has been answered. Set window.DD_ONE_AT_A_TIME = false (or
     ?steps=all) to show a whole page at once. */
  var ONE_AT_A_TIME = qs.get("steps") === "all" ? false
    : (window.DD_ONE_AT_A_TIME === undefined ? true : !!window.DD_ONE_AT_A_TIME);

  // Jotform's "today" field. The engine computes age as
  //   425 = floor(((403 - 268) / 30.417) / 12)
  // and the SERVER stamps 403 on every /score and /evaluate. We mirror the same
  // arithmetic here purely so the age READOUT updates the instant a birth date
  // is typed — the scored value always comes from the server.
  var TODAY_QID = "403", DOB_QID = "268", AGE_QID = "425", AGE_SHOWN_QID = "412";
  // Everything the engine derives from the age. These are recomputed here the
  // moment the birth date changes and cleared when it is removed — see
  // refreshLocalAge for why the server's copy alone is not safe to gate on.
  var AGE_DERIVED = ["425", "412", "1164", "1165", "1166", "1167"];
  function dayNumber(v) {
    var t = Date.parse(String(v || "").slice(0, 10));
    return isNaN(t) ? null : Math.floor(t / 86400000);
  }
  function ageFromDob(dob) {
    var b = dayNumber(dob), n = dayNumber(new Date().toISOString());
    if (b == null || n == null) return null;
    return Math.floor(((n - b) / 30.417) / 12);
  }

  // ---- state -----------------------------------------------------------------
  var answers = {};        // qid -> value (GLOBAL — persists across every page)
  var spec = null;         // form-spec.json
  var pageByJf = {};       // jfPage -> page spec
  var rendered = [];       // [{ item, el }] for the page on screen (intra-page gating)
  var currentJf = null;    // jfPage currently shown
  var currentCard = 0;     // which card OF that page is on screen (see splitCards)
  var visiblePages = [];   // ordered visible jfPage list from the last /evaluate
  // Engine-derived helpers the questionnaire never asks for but the gating rules
  // read (425 age, 1162 gender, 1164 minor/adult, 2288 skin-tone band, …).
  // Supplied by /evaluate; see form-spec.json _gating.computedFields.
  var computed = {};
  var computeTimer = null;
  var firstPaint = true;    // suppress the reveal animation on the first render

  // ---- tiny DOM helpers ------------------------------------------------------
  function el(tag, cls, txt) { var e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; }
  function cap(s) { s = String(s || ""); return s.charAt(0).toUpperCase() + s.slice(1); }

  // ---- intra-page field gating -----------------------------------------------
  // (page-level visibility is the server's job; this is field-level show/hide
  //  WITHIN the page on screen, reading from the global answers map so a rule
  //  may reference a field answered on an earlier page.)
  // A rule may test a field the respondent answered OR one the engine derived.
  function valueOf(qid) {
    var q = String(qid);
    if (answers[q] != null && answers[q] !== "") return answers[q];
    if (computed[q] != null) return computed[q];
    return "";
  }
  // Jotform's operator spellings, verbatim as the spec stores them. The old
  // version tested for "gt"/"lt" — spellings that never appear — so every
  // greaterThan/lessThan rule silently fell through to a substring match.
  function termTrue(t) {
    var raw = valueOf(t.field);
    var have = String(raw == null ? "" : raw).toLowerCase();
    var want = String(t.value == null ? "" : t.value).toLowerCase();
    switch (String(t.op || "").toLowerCase()) {
      case "contains":                       return have.indexOf(want) !== -1;
      case "notcontains": case "doesntcontain": return have.indexOf(want) === -1;
      case "equals":      case "is":         return have === want;
      case "notequals":   case "isnot":      return have !== want;
      case "isempty":                        return have === "";
      case "isfilled":    case "isnotempty": return have !== "";
      case "startswith":                     return have.indexOf(want) === 0;
      case "endswith":                       return want === "" || have.slice(-want.length) === want;
      // numeric: parseFloat tolerates the engine's "42 years" style values
      case "greaterthan": case "gt":         return parseFloat(have) >  parseFloat(want);
      case "lessthan":    case "lt":         return parseFloat(have) <  parseFloat(want);
      default:                               return have.indexOf(want) !== -1;
    }
  }
  function ruleMet(rule) {
    var terms = rule.when || [];
    if (!terms.length) return false;
    return (String(rule.link || "All").toLowerCase() === "any")
      ? terms.some(termTrue) : terms.every(termTrue);
  }
  // Jotform semantics: a field is shown when ANY Show condition is met, and a
  // met Hide condition wins over everything. The old version AND-ed the rules
  // (gatedBy.every), which hid any field carrying two alternative Show rules.
  function visible(item) {
    var rules = item.gatedBy || [];
    if (!rules.length) return !item.hiddenByDefault;
    var shows = [], hides = [];
    for (var i = 0; i < rules.length; i++) (rules[i].show ? shows : hides).push(rules[i]);
    for (var h = 0; h < hides.length; h++) if (ruleMet(hides[h])) return false;
    if (!shows.length) return true;             // hide-only rules, none met
    for (var k = 0; k < shows.length; k++) if (ruleMet(shows[k])) return true;
    return false;
  }
  function applyGating() {
    var pruned = false;
    for (var pass = 0; pass < 12; pass++) {      // clearing one answer can hide the next
      rendered.forEach(function (r) {
        // gate inputs AND display blocks — conditional warnings (hiddenByDefault,
        // e.g. the age-verification notices) must stay hidden until their rule fires
        if (r.item.kind !== "input" && r.item.kind !== "display") return;
        r.el.hidden = !visible(r.item);
      });
      applyFamilyFallback();
      if (!pruneHidden()) break;
      pruned = true;
    }
    applyProgressive();
    if (pruned) refreshComputed();
  }

  /* ---- a hidden question has no answer -----------------------------------------
     Found 2026-10-04: answer "not pregnant", "none of the above", pill "no" — the
     IUD question appears. Change the menopause answer to "postmenopausal": the
     pill question hides, but its "no" stayed on file, so the IUD question (gated
     on pill = no) stayed on screen, required, for a woman it no longer applies
     to. The same stale answer would also have been SCORED.

     So when a rule hides a question, its answer goes, and the widgets on screen
     are reset to match — and because clearing one answer can hide another, the
     caller repeats until nothing changes.

     One exception. A rule that reads a value only the server computes can be
     briefly wrong while /evaluate is in flight (changing the birth date clears
     the age-derived fields until the reply lands). Clearing on that transient
     would delete an answer the customer just gave, so those items wait; the
     reply runs applyGating again and they are judged on fresh values.
     Answers on pages the respondent never reaches are dealt with at submit
     (pruneForSubmit), where the server's verdict is current. */
  var computePending = false, computeSeq = 0;
  var serverOnly = null;
  function readsServerValue(item) {
    if (!serverOnly) {
      serverOnly = {};
      ((spec && spec._gating && spec._gating.computedFields) || []).forEach(function (q) { serverOnly[q] = true; });
    }
    var rules = item.gatedBy || [];
    for (var i = 0; i < rules.length; i++) {
      var w = rules[i].when || [];
      for (var j = 0; j < w.length; j++) if (serverOnly[String(w[j].field)]) return true;
    }
    return false;
  }
  function clearInputs(root) {
    var ins = root.querySelectorAll("input, textarea, select");
    for (var i = 0; i < ins.length; i++) {
      var t = ins[i].type;
      if (t === "radio" || t === "checkbox") ins[i].checked = false;
      else { ins[i].value = ""; ins[i].__ddLen = 0; }
    }
    var sel = root.querySelectorAll(".is-sel");
    for (var k = 0; k < sel.length; k++) sel[k].classList.remove("is-sel");
  }
  function pruneHidden() {
    var changed = false;
    rendered.forEach(function (r) {
      var it = r.item;
      if (it.kind !== "input" || !r.el.hidden || it.engineOwned || it.type === "computed") return;
      if (answers[it.qid] == null || answers[it.qid] === "") return;
      if (computePending && readsServerValue(it)) return;
      delete answers[it.qid];
      answersVersion++;
      clearInputs(r.el);
      changed = true;
    });
    return changed;
  }
  /* At submit, with /evaluate just answered: drop every answer whose question
     the respondent would not be shown now — on a page that dropped out of their
     path (say they changed their gender after answering the pregnancy page) or
     hidden by its own rule. Picker families are left to the screen, where the
     fallback that may have shown them is applied. */
  function pruneForSubmit(vis) {
    var removed = [];
    for (var pass = 0; pass < 12; pass++) {
      var before = removed.length;
      spec.pages.forEach(function (p) {
        var onPath = vis.indexOf(p.jfPage) !== -1;
        p.items.forEach(function (it) {
          if (it.kind !== "input" || it.engineOwned || it.type === "computed") return;
          if (answers[it.qid] == null || answers[it.qid] === "") return;
          if (onPath && (it.family || visible(it))) return;
          delete answers[it.qid];
          removed.push(it.qid);
        });
      });
      if (removed.length === before) break;
    }
    return removed;
  }

  /* ---- one question at a time -------------------------------------------------
     Rule-based gating above decides what COULD be shown; this decides how much of
     it is shown yet. Walking the page in order, everything after the first
     unanswered required question is held back, so the respondent answers one
     thing before being shown the next.

     Three things deliberately do not hold the page back:
       • optional questions — nothing would ever reveal the rest of the page;
       • `computed` readouts (the age) — nobody can "answer" one;
       • notices — safety information is never withheld behind another question.
     A question hidden by its own rule is skipped entirely, so a branch the
     respondent isn't on can't stall them. */
  function gatesProgress(item) {
    return item.kind === "input" && item.type !== "computed" && item.required;
  }
  /* A picker family (skin tone / gender variants of one question) whose
     discriminator has no value would otherwise show NO variant at all, silently
     dropping the question — a respondent with no recorded skin colour saw no
     acne-type picker whatsoever. When the discriminator is genuinely empty, fall
     back to the family's default. If the discriminator HAS a value and every
     variant is still hidden, that is deliberate logic (e.g. comedones-only skips
     the grade picker) and is left alone. */
  function applyFamilyFallback() {
    var fam = {};
    rendered.forEach(function (r) {
      if (!r.item.family) return;
      (fam[r.item.family] = fam[r.item.family] || []).push(r);
    });
    Object.keys(fam).forEach(function (name) {
      var members = fam[name];
      if (members.some(function (r) { return !r.el.hidden; })) return;   // one is showing
      var field = members[0].item.familyField;
      if (String(valueOf(field) || "").trim() !== "") return;            // deliberately hidden
      var def = null;
      for (var i = 0; i < members.length; i++) if (members[i].item.familyDefault) def = members[i];
      (def || members[0]).el.hidden = false;
    });
  }

  function applyProgressive() {
    if (!ONE_AT_A_TIME) return;
    var gated = false;
    for (var i = 0; i < rendered.length; i++) {
      var r = rendered[i], item = r.item;
      if (item.kind !== "input" && item.kind !== "display") continue;
      if (r.el.hidden) continue;                       // its rule already hid it
      if (gated && !(item.kind === "display" && item.notice)) { r.el.hidden = true; continue; }
      if (gatesProgress(item) && !answered(item)) gated = true;
    }
    markReveals();
  }

  /* Fade each newly-revealed block in, and bring it into view only if it is
     actually off-screen (block:"nearest" scrolls the minimum needed, so a page
     that already fits doesn't jump). */
  function markReveals() {
    for (var i = 0; i < rendered.length; i++) {
      var el0 = rendered[i].el, showing = !el0.hidden;
      if (showing === !!el0.__ddShown) continue;
      el0.__ddShown = showing;
      if (!showing) continue;
      if (!firstPaint) {
        el0.classList.add("dd-reveal");
        (function (n) { setTimeout(function () { n.classList.remove("dd-reveal"); }, 500); })(el0);
        if (el0.scrollIntoView) { try { el0.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) {} }
      }
    }
    firstPaint = false;
  }

  // ---- value capture ---------------------------------------------------------
  function setVal(qid, v) {
    if (v == null || v === "") delete answers[qid]; else answers[qid] = v;
    answersVersion++;
    if (qid === DOB_QID) {
      for (var d = 0; d < AGE_DERIVED.length; d++) delete computed[AGE_DERIVED[d]];
      refreshLocalAge();
    }
    applyGating();
    paintComputedReadouts();
    clearError(qid);
    refreshComputed();
  }

  /* Work the age out here, immediately, rather than waiting on /evaluate. It
     uses the engine's own arithmetic so the two can't disagree, and it means the
     age (and the question asking you to confirm it) appear the moment a birth
     date is complete instead of a network round-trip later. */
  function refreshLocalAge() {
    var i;
    var a = ageFromDob(answers[DOB_QID]);
    if (a == null || a < 0 || a > 130) {
      for (i = 0; i < AGE_DERIVED.length; i++) delete computed[AGE_DERIVED[i]];
      return;
    }
    computed[AGE_QID] = computed[AGE_SHOWN_QID] = a + " years";
    /* 1164 and 1165 MUST be derived here too, not left to the server.
       The under-18 notice fires on `425 > 7 AND 1164 = "minor"`. Deriving 425
       locally but taking 1164 from the last /evaluate leaves a window where the
       age is fresh and minor/adult is stale — correct a teen's birth date to an
       adult one and, until the next round-trip answers, a 42-year-old is shown
       the "you are under 18, a guardian must complete this" notice and its
       checkbox. If the request is slow, dropped or the page is offline, it never
       clears. Same thresholds as the engine, including its 39-year gap, so the
       two can't disagree. */
    computed["1164"] = a < 18 ? "minor" : "adult";
    // Bands transcribed exactly from the engine's own guards, gaps included:
    // elderly >69 | older-adult >59 <70 | middle-aged-adult >39 <60
    // adult >29 <39 | young-adult >19 <30 | teenager >12 <20 | tween >6 <13
    // Note 39 falls through every band in the live form. Reproduced rather than
    // tidied, so the client can never disagree with the server about a band.
    var band = a > 69 ? "elderly"
             : (a > 59 && a < 70) ? "older-adult"
             : (a > 39 && a < 60) ? "middle-aged-adult"
             : (a > 29 && a < 39) ? "adult"
             : (a > 19 && a < 30) ? "young-adult"
             : (a > 12 && a < 20) ? "teenager"
             : (a > 6  && a < 13) ? "tween"
             : "";
    if (band) computed["1165"] = band; else delete computed["1165"];
    /* 1166 and 1167 are NOT touched here. They are the server's to derive —
       1167 ("does the gynaecological history apply") needs the gender answer as
       well as the age, which is why it cannot be worked out locally. This
       function runs immediately after `computed = j.computed`, so deleting them
       here threw away the value that had just arrived, every single time: 1167
       was never present when the gate for "I am pregnant" (375) was tested, so
       that question never appeared — and 376, 379 and 381 all hang off it, so
       "My Gynecological History" rendered as a heading with nothing under it.
       Staleness is already handled where it actually arises: changing the birth
       date clears the whole AGE_DERIVED list in setVal before calling this, and
       an unreadable date clears it in the guard above. */
  }

  /* A value carrying no digits is not an age. The engine renders 425 as
     "<number> years", so when it has nothing to work with it returns a bare
     " years" — which is what put a lone "years" on screen. Worse, given a
     stamped "today" and NO birth date it happily returns "56 years". Neither is
     allowed to reach the page or the gating. */
  function hasNumber(v) { return /\d/.test(String(v == null ? "" : v)); }

  /* Rules that read engine-derived fields need the server. Debounced so typing a
     birth date is one request, not eight — and gating that depends only on raw
     answers has already updated synchronously above. */
  function refreshComputed() {
    if (computeTimer) clearTimeout(computeTimer);
    computePending = true;
    computeTimer = setTimeout(function () {
      computeTimer = null;
      var mine = ++computeSeq;       // replies can arrive out of order; only the latest counts
      var sentV = answersVersion;
      fetch(EVALUATE_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: answers })
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (mine !== computeSeq || computeTimer) return;   // superseded by a newer request
          computePending = false;
          if (!j || !j.ok || !j.computed) { applyGating(); return; }
          computed = j.computed;
          if (Array.isArray(j.pages) && j.pages.length && sentV === answersVersion) lastEval = { v: sentV, pages: j.pages };
          if (!hasNumber(computed[AGE_QID])) { delete computed[AGE_QID]; delete computed[AGE_SHOWN_QID]; }
          refreshLocalAge();          // the local value wins; it can't be stale
          applyGating();
          paintComputedReadouts();
        })
        .catch(function () { if (mine === computeSeq && !computeTimer) computePending = false; /* offline or blocked: keep the last known map */ });
    }, 260);
  }

  // ---- renderers per input kind (each restores its current global answer) ----
  /* Image pickers. Twelve of them take MORE than one answer — "select all that
     apply": breakout types, and the area pickers for acne, redness and
     pigmentation. They were all rendering as radios, so a respondent could only
     ever report one. `multi` comes from the spec (see gen-formspec-gating.py,
     which reads it off real submissions). The value is the selected short
     labels joined by newlines, which is exactly what the engine splits on. */
  /* What a tile SAYS, not what it stores. The keys come from the Jotform widgets
     as typed — "red" beside "MEDIUM-BROWN" beside "Black" on the same hair
     page — and are matched by the engine, so they stay exactly as they are.
     The label is lowercased here and capitalised by .dd-tilelabel, so every
     tile reads the same way: "Medium Brown", "Red", "Dry Skin Patches". */
  function tileLabel(key) { return cap(String(key).replace(/[-_]+/g, " ").toLowerCase()); }

  function renderWidget(item) {
    var items = item.widgetItems;
    if (!items || (Array.isArray(items) ? !items.length : !Object.keys(items).length)) return null;
    var entries = Array.isArray(items)
      ? items.map(function (k) { return [k, null]; })
      : Object.keys(items).map(function (k) { return [k, items[k]]; });
    var multi = !!item.multi;
    var chosen = String(answers[item.qid] == null ? "" : answers[item.qid])
      .split(/\r?\n/).map(function (x) { return x.trim(); }).filter(Boolean);

    var fs = el("fieldset", "dd-field dd-gated");
    if (item.label && item.label !== "*") fs.appendChild(el("legend", null, item.label));
    if (multi) fs.appendChild(el("p", "dd-hint", "Select all that apply."));
    var grid = el("div", "dd-tiles");
    var inputs = [];
    entries.forEach(function (pair) {
      var key = pair[0], url = pair[1];
      var tile = el("label", "dd-tile");
      var input = el("input");
      input.type = multi ? "checkbox" : "radio";
      input.name = "q" + item.qid; input.value = key;
      if (chosen.indexOf(key) !== -1) { input.checked = true; tile.classList.add("is-sel"); }
      tile.appendChild(input);
      if (url) { var img = el("img"); img.src = url; img.alt = tileLabel(key); tile.appendChild(img); }
      tile.appendChild(el("div", "dd-tilelabel", tileLabel(key)));
      // the definition, on the card, where the decision is made
      var note = item.optionText && item.optionText[key];
      if (note) tile.appendChild(el("div", "dd-tiletext", note));
      if (url) {
        var zoom = el("button", "dd-zoom", "\u2315");
        zoom.type = "button";
        zoom.setAttribute("aria-label", "Enlarge " + tileLabel(key));
        zoom.addEventListener("click", function (ev) {
          ev.preventDefault(); ev.stopPropagation();
          openLightbox(url, tileLabel(key), note);
        });
        tile.appendChild(zoom);
      }
      inputs.push({ input: input, tile: tile, key: key });
      input.addEventListener("change", function () {
        if (multi) {
          var picked = [];
          inputs.forEach(function (o) {
            o.tile.classList.toggle("is-sel", o.input.checked);
            if (o.input.checked) picked.push(o.key);   // option order, not click order
          });
          setVal(item.qid, picked.join("\n"));
        } else {
          inputs.forEach(function (o) { o.tile.classList.remove("is-sel"); });
          tile.classList.add("is-sel");
          setVal(item.qid, key);   // the short label; the engine decorates it
        }
      });
      grid.appendChild(tile);
    });
    fs.appendChild(grid);

    /* Somewhere to put honest uncertainty. The pickers are required, so without
       this a respondent who genuinely cannot tell has to guess — and a guess is
       worse than a recorded "don't know" for a clinical algorithm. It is stored
       as its own value, matches no scoring rule, and no rule counts these fields
       (count() is only used on 392, 1150, 1294, 1673 and 1873), so it satisfies
       the required check without contributing a symptom.

       Not everywhere, though. Gender, and natural skin, eye and hair colour are
       things a person knows about themselves and can see in a mirror — offering
       "I'm not sure" there invites a shrug on the four answers the Fitzpatrick
       typing leans on hardest, and buys nothing in return. */
    if (NO_UNSURE[String(item.qid)]) return fs;

    var unsure = el("label", "dd-unsure");
    var ui = el("input"); ui.type = "checkbox"; ui.name = "q" + item.qid + "-unsure";
    ui.checked = chosen.indexOf(UNSURE) !== -1;
    if (ui.checked) unsure.classList.add("is-sel");
    unsure.appendChild(ui);
    unsure.appendChild(el("span", "dd-dot"));
    unsure.appendChild(el("span", "dd-optlabel", "I'm not sure"));
    ui.addEventListener("change", function () {
      unsure.classList.toggle("is-sel", ui.checked);
      if (ui.checked) {
        inputs.forEach(function (o) { o.input.checked = false; o.tile.classList.remove("is-sel"); });
        setVal(item.qid, UNSURE);
      } else {
        setVal(item.qid, "");
      }
    });
    inputs.forEach(function (o) {
      o.input.addEventListener("change", function () {
        if (o.input.checked && ui.checked) { ui.checked = false; unsure.classList.remove("is-sel"); }
      });
    });
    fs.appendChild(unsure);
    return fs;
  }

  var UNSURE = "not sure";
  var TERMS_QID = "116";   // the acceptance radio on the last page
  /* Pickers that do NOT offer "I'm not sure": gender (214), natural skin colour
     (243, 244), natural eye colour (245, 248) and natural hair colour (the
     twelve variants on 300-305 / 355-360). Held as qids rather than page
     numbers so re-ordering the form cannot silently re-introduce the option. */
  var NO_UNSURE = {
    "214": 1,
    "243": 1, "244": 1,
    "245": 1, "248": 1,
    "300": 1, "301": 1, "302": 1, "303": 1, "304": 1, "305": 1,
    "355": 1, "356": 1, "357": 1, "358": 1, "359": 1, "360": 1,
    /* The three body-map pickers. You can see where your own breakouts and
       redness are; "I'm not sure" on a map of your own face is a shrug, and it
       lands on the fields the acne and rosacea scores lean on hardest.
         812 / 813   where breakouts or congested pores are
         2007 / 2009 which part of the face the breakouts are on
         789 / 791   which regions are red
         1038 / 1048 where the hyperpigmentation is
       Two qids each, because the male and female image sets are separate
       questions gated on 1162. */
    "812": 1, "813": 1,
    /* Breakout types and breakout severity (2026-10-04, Trinette). They are only
       asked of someone who has just said they have blackheads, whiteheads or
       pimples, every tile carries a picture and a definition, and an "unsure"
       here leaves the engine nothing to grade acne on — no diagnosis to give.
         1726 / 1727 / 1728   type/s of breakouts (light / medium / dark skin)
         1719 / 1731 / 1732   amount of breakouts at any one time */
    "1726": 1, "1727": 1, "1728": 1,
    "1719": 1, "1731": 1, "1732": 1,
    "2007": 1, "2009": 1,
    "789": 1, "791": 1,
    "1038": 1, "1048": 1
  };

  /* Tap to enlarge — these are small circles carrying fine detail. */
  function openLightbox(url, title, note) {
    var back = el("div", "dd-lightbox");
    var box = el("div", "dd-lightbox__in");
    var img = el("img"); img.src = url; img.alt = title;
    box.appendChild(img);
    box.appendChild(el("h3", null, title));
    if (note) box.appendChild(el("p", null, note));
    var close = el("button", "dd-lightbox__x", "\u00d7");
    close.type = "button"; close.setAttribute("aria-label", "Close");
    box.appendChild(close);
    back.appendChild(box);
    function shut() { if (back.parentNode) back.parentNode.removeChild(back); document.removeEventListener("keydown", key); }
    function key(e) { if (e.key === "Escape") shut(); }
    back.addEventListener("click", function (e) { if (e.target === back) shut(); });
    close.addEventListener("click", shut);
    document.addEventListener("keydown", key);
    document.body.appendChild(back);
    close.focus();
  }

  /* "None of the above" in a select-all-that-apply question.

     Nothing stopped a respondent ticking "I don't have any known allergies" AND
     "eat certain foods" — not here and not in the live Jotform, which is a plain
     checkbox group with no enforcement either. The allergy rules count() the
     selections, so a contradictory pair would have added the denial to the tally
     it contradicts. `exclusive` is set on the option by gen-formspec-gating.py
     (anchored pattern, one option per question or it refuses and reports), so
     this stays generic rather than hard-coding qid 392. */
  function enforceExclusive(box, changed) {
    var all = Array.prototype.slice.call(box.querySelectorAll("input[type=checkbox]"));
    if (!changed.checked) return;
    var changedIsX = changed.getAttribute("data-exclusive") === "1";
    all.forEach(function (i) {
      if (i === changed) return;
      var isX = i.getAttribute("data-exclusive") === "1";
      // ticking the "none" option clears the rest; ticking any of the rest clears "none"
      if (changedIsX || isX) i.checked = false;
    });
  }

  function renderChoice(item, multi) {
    var fs = el("fieldset", "dd-field dd-gated");
    if (item.label) fs.appendChild(el("legend", null, item.label));
    /* Say so when more than one answer is allowed — the picture pickers always
       did; the plain lists did not, and their round markers read as "pick one".
       The modifier class squares the markers off, the convention for "several". */
    if (multi) fs.appendChild(el("p", "dd-hint", "Select all that apply."));
    var box = el("div", multi ? "dd-opts dd-opts--multi" : "dd-opts");
    var checked = multi ? String(answers[item.qid] || "").split("\n") : null;
    (item.options || []).forEach(function (o) {
      var opt = el("label", "dd-opt");
      var input = el("input"); input.type = multi ? "checkbox" : "radio";
      input.name = "q" + item.qid; input.value = o.value;
      if (multi && o.exclusive) input.setAttribute("data-exclusive", "1");
      if (multi ? checked.indexOf(o.value) !== -1 : answers[item.qid] === o.value) {
        input.checked = true; opt.classList.add("is-sel");
      }
      opt.appendChild(input);
      opt.appendChild(el("span", "dd-dot"));
      /* `value` is what gets stored and what every rule, calculation and page
         gate matches on; `label` is display-only, for options whose stored
         wording is not what a respondent should read. */
      // first letter capitalised on screen only: most stored values are lowercase
      // ("yes", "postmenopausal") and the engine matches them exactly as stored
      opt.appendChild(el("span", "dd-optlabel", cap(o.label || o.value)));
      input.addEventListener("change", function () {
        if (multi) {
          enforceExclusive(box, input);
          var vals = Array.prototype.slice.call(box.querySelectorAll("input:checked")).map(function (i) { return i.value; });
          box.querySelectorAll(".dd-opt").forEach(function (l) { l.classList.toggle("is-sel", l.querySelector("input").checked); });
          setVal(item.qid, vals.join("\n"));
        } else {
          box.querySelectorAll(".dd-opt").forEach(function (l) { l.classList.remove("is-sel"); });
          opt.classList.add("is-sel");
          setVal(item.qid, o.value);
        }
      });
      box.appendChild(opt);
    });
    fs.appendChild(box);
    return fs;
  }

  function renderText(item, type) {
    var wrap = el("div", "dd-field dd-gated");
    wrap.appendChild(el("div", "dd-q", item.label || item.name || ("Field " + item.qid)));
    var input = el("input", "dd-input");
    input.type = type || "text"; input.name = "q" + item.qid;
    if (answers[item.qid] != null) input.value = answers[item.qid];
    input.addEventListener("input", function () { setVal(item.qid, input.value.trim()); });
    wrap.appendChild(input);
    return wrap;
  }

  /* ---- date of birth: three boxes, not the browser's date picker -------------
     Reported 2026-10-04: type 1983, then 12, and the year becomes 198312.

     That was <input type="date">, and it is the wrong control for a birth date:
     - Chrome's year segment takes up to SIX digits (it allows years to 275760),
       so it never knows a year is finished and never moves on to the month.
     - On a phone it opens a calendar or wheel that starts at today, so a
       42-year-old scrolls back through 500 months to reach their birthday.
     - Its segment order follows the device's language setting, so the same
       page reads YYYY/MM/DD on one phone and MM/DD/YYYY on the next.
     A date you already know is fastest typed. Three numeric boxes give a number
     pad on phones, one fixed order everywhere, and let the browser autofill each
     part from the customer's saved details (bday-year / bday-month / bday-day).

     Moving on by itself is done carefully, because auto-advance done badly is
     worse than none (it is why GOV.UK advises against it):
     - it only moves FORWARD, and only when a box was just typed into and is now
       complete. Deleting never moves you, so fixing a digit is ordinary editing.
     - a month of 2-9 can only be one digit, so it moves on at once.
     - typing - / . or a space also moves on, for people who type separators.
     - Backspace at the start of a box steps back and keeps deleting there.
     - pasting a whole date into any box fills all three: 1984-03-15,
       15/03/1984 (day first, as written in South Africa) or 19840315.
     - nothing is flagged while a box is still being typed — only once a box is
       complete and impossible (month 13), or when you leave the date entirely.
     The answer stored is still ISO YYYY-MM-DD — exactly what the engine and
     every test case use — and it is only set once the date is a real one. */

  /* dd-date:begin — pure helpers; worker/test/form.test.mjs runs this block */
  var DATE_PARTS = [
    { key: "y", label: "Year",  len: 4, ph: "YYYY", ac: "bday-year"  },
    { key: "m", label: "Month", len: 2, ph: "MM",   ac: "bday-month" },
    { key: "d", label: "Day",   len: 2, ph: "DD",   ac: "bday-day"   }
  ];
  var MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July",
    "August", "September", "October", "November", "December"];
  function pad2(n) { n = String(n); return n.length < 2 ? "0" + n : n; }
  function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
  // the respondent's own calendar day, not UTC's — 00:30 in Pretoria is still yesterday in UTC
  function todayIso(now) {
    now = now || new Date();
    return now.getFullYear() + "-" + pad2(now.getMonth() + 1) + "-" + pad2(now.getDate());
  }
  /* y, m, d: what is in the three boxes (digits only). done: the respondent has
     left the date, so an unfinished box is now a mistake rather than work in
     progress. today: "YYYY-MM-DD".
     Returns { iso } for a real date, { err, part } for one that cannot be, and
     {} while it is still being typed. */
  function checkDate(y, m, d, done, today) {
    y = String(y || ""); m = String(m || ""); d = String(d || "");
    if (!y && !m && !d) return done ? { err: "Enter your date of birth", part: "y" } : {};
    var yn = +y, mn = +m, dn = +d, ty = +today.slice(0, 4);
    // a box that is full and impossible is said straight away
    if (y.length === 4 && yn > ty) return { err: "Check the year — it can’t be after " + ty, part: "y" };
    if (y.length === 4 && yn < 1900) return { err: "Check the year — for example 1984", part: "y" };
    if (m.length === 2 && (mn < 1 || mn > 12)) return { err: "The month must be between 01 and 12", part: "m" };
    if (d.length === 2 && (dn < 1 || dn > 31)) return { err: "The day must be between 01 and 31", part: "d" };
    /* "1" in the month box may be the start of 10-12, and "1"-"3" in the day box
       the start of 10-31, so a lone digit there is finished only once they
       leave. A month of 2-9 or a day of 4-9 cannot be anything else. */
    var whole = y.length === 4 && m && d &&
      (done || ((m.length === 2 || mn > 1) && (d.length === 2 || dn > 3)));
    if (!whole) {
      if (!done) return {};
      if (y.length !== 4) return { err: "Enter the year as four digits — for example 1984", part: "y" };
      if (!m) return { err: "Enter the month", part: "m" };
      return { err: "Enter the day", part: "d" };
    }
    if (mn < 1 || mn > 12) return { err: "The month must be between 01 and 12", part: "m" };
    if (dn < 1) return { err: "The day must be between 01 and 31", part: "d" };
    var max = daysInMonth(yn, mn);
    if (dn > max) return { err: MONTH_NAMES[mn - 1] + " " + yn + " only has " + max + " days", part: "d" };
    var iso = y + "-" + pad2(mn) + "-" + pad2(dn);
    if (iso > today) return { err: "Your date of birth can’t be in the future", part: "d" };
    return { iso: iso };
  }
  /* A whole date pasted or autofilled into one box. Year first is unambiguous;
     with the year last the day comes first, as South Africans write it. */
  function parseDateText(s) {
    s = String(s || "").trim();
    var a = /^(\d{4})\D+(\d{1,2})\D+(\d{1,2})$/.exec(s);
    if (a) return { y: a[1], m: pad2(+a[2]), d: pad2(+a[3]) };
    a = /^(\d{1,2})\D+(\d{1,2})\D+(\d{4})$/.exec(s);
    if (a) return { y: a[3], m: pad2(+a[2]), d: pad2(+a[1]) };
    if (/^\d{8}$/.test(s)) {
      return /^(19|20)/.test(s) ? { y: s.slice(0, 4), m: s.slice(4, 6), d: s.slice(6) }
                                : { y: s.slice(4), m: s.slice(2, 4), d: s.slice(0, 2) };
    }
    return null;
  }
  /* dd-date:end */

  function renderDate(item) {
    var qid = item.qid;
    var wrap = el("div", "dd-field dd-gated dd-date");
    var q = el("div", "dd-q", item.label || "Date of birth");
    q.id = "dd-q-" + qid;
    var hint = el("p", "dd-hint", "For example 1984 03 15");
    hint.id = "dd-hint-" + qid;
    wrap.setAttribute("role", "group");
    wrap.setAttribute("aria-labelledby", q.id);
    wrap.setAttribute("aria-describedby", hint.id);
    wrap.appendChild(q);
    wrap.appendChild(hint);

    var row = el("div", "dd-date-row"), boxes = [];
    DATE_PARTS.forEach(function (p) {
      var cell = el("div", "dd-date-part dd-date-" + p.key);
      var inp = el("input", "dd-input dd-date-input");
      inp.id = "q" + qid + "-" + p.key;
      inp.name = "q" + qid + "_" + p.key;
      inp.type = "text";                       // not "number": no spinner, no 1e5, keeps a leading 0
      inp.setAttribute("inputmode", "numeric");
      inp.setAttribute("pattern", "[0-9]*");   // older iOS needs this for the number pad
      inp.setAttribute("autocomplete", p.ac);
      inp.setAttribute("placeholder", p.ph);
      inp.setAttribute("aria-describedby", hint.id);
      var lab = el("label", "dd-date-label", p.label);
      lab.htmlFor = inp.id;
      cell.appendChild(lab);
      cell.appendChild(inp);
      row.appendChild(cell);
      boxes.push(inp);
    });
    wrap.appendChild(row);

    var have = /^(\d{4})-(\d{2})-(\d{2})/.exec(answers[qid] || "");
    if (have) { boxes[0].value = have[1]; boxes[1].value = have[2]; boxes[2].value = have[3]; }
    boxes.forEach(function (b) { b.__ddLen = b.value.length; });

    function read(done) { return checkDate(boxes[0].value, boxes[1].value, boxes[2].value, done, todayIso()); }
    function hideErr() {
      wrap.classList.remove("has-err");
      var old = wrap.querySelectorAll(".dd-fielderr");
      for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
      boxes.forEach(function (b) { b.removeAttribute("aria-invalid"); b.setAttribute("aria-describedby", hint.id); });
    }
    function showErr(r) {
      hideErr();
      if (!r.err) return;
      var msg = el("p", "dd-fielderr", r.err);
      msg.id = "dd-err-" + qid;
      msg.setAttribute("role", "alert");
      wrap.classList.add("has-err");
      wrap.appendChild(msg);
      var bad = boxes["ymd".indexOf(r.part)] || boxes[0];
      bad.setAttribute("aria-invalid", "true");
      bad.setAttribute("aria-describedby", hint.id + " " + msg.id);
    }
    /* Store the date only when it is real, and clear it the moment it is not —
       the age readout and the age-gated notices must never show a stale age. */
    function commit(done) {
      var r = read(done), v = r.iso || "";
      if ((answers[qid] || "") !== v) setVal(qid, v);   // setVal clears errors, so it goes first
      showErr(r);
      return r;
    }
    function caretTo(b, pos) { try { b.setSelectionRange(pos, pos); } catch (e) {} }
    function goTo(i) { boxes[i].focus(); try { boxes[i].select(); } catch (e) {} }
    // fills from box i onwards (a pasted run of digits, or typing that outran the box); returns where to land
    function spill(i, digits) {
      for (; i < boxes.length; i++) {
        var len = DATE_PARTS[i].len, take = digits.slice(0, len);
        boxes[i].value = take; boxes[i].__ddLen = take.length;
        digits = digits.slice(len);
        if (!digits) return (take.length === len && i < boxes.length - 1) ? i + 1 : i;
      }
      return boxes.length - 1;
    }

    boxes.forEach(function (inp, i) {
      var len = DATE_PARTS[i].len, last = i === boxes.length - 1;

      inp.addEventListener("input", function () {
        var raw = inp.value, digits = raw.replace(/\D/g, "");
        var sep = /\d\D+$/.test(raw);                    // digits, then - / . or a space
        var grew = digits.length > (inp.__ddLen || 0);
        var land = i;
        if (digits.length > len) {
          land = spill(i, digits);
        } else {
          if (raw !== digits) inp.value = digits;
          inp.__ddLen = digits.length;
          if ((grew || sep) && !last && digits &&
              (digits.length === len || sep || (i === 1 && +digits > 1))) land = i + 1;
        }
        commit(false);
        if (land !== i && document.activeElement === inp) goTo(land);
      });

      inp.addEventListener("keydown", function (e) {
        var atStart = inp.selectionStart === 0 && inp.selectionEnd === 0;
        var atEnd = inp.selectionStart === inp.value.length && inp.selectionEnd === inp.value.length;
        if (e.key === "Backspace" && i > 0 && atStart) {
          e.preventDefault();
          var prev = boxes[i - 1];
          prev.value = prev.value.slice(0, -1); prev.__ddLen = prev.value.length;
          prev.focus(); caretTo(prev, prev.value.length);
          commit(false);
        } else if (e.key === "ArrowLeft" && i > 0 && atStart) {
          e.preventDefault(); boxes[i - 1].focus(); caretTo(boxes[i - 1], boxes[i - 1].value.length);
        } else if (e.key === "ArrowRight" && !last && atEnd) {
          e.preventDefault(); boxes[i + 1].focus(); caretTo(boxes[i + 1], 0);
        }
      });

      inp.addEventListener("paste", function (e) {
        var cd = e.clipboardData || window.clipboardData;
        var p = parseDateText(cd && cd.getData("text"));
        if (!p) return;                                  // not a whole date: the input handler tidies it
        e.preventDefault();
        boxes[0].value = p.y; boxes[1].value = p.m; boxes[2].value = p.d;
        boxes.forEach(function (b) { b.__ddLen = b.value.length; });
        boxes[2].focus(); caretTo(boxes[2], boxes[2].value.length);
        commit(false);
      });

      // "4" means April: write it as 04 once they move on
      inp.addEventListener("blur", function () {
        if (i > 0 && /^[1-9]$/.test(inp.value)) { inp.value = "0" + inp.value; inp.__ddLen = 2; }
      });
    });

    // leaving the date as a whole (not just moving between its boxes) is when an unfinished one is flagged
    wrap.addEventListener("focusout", function (e) {
      if (e.relatedTarget && wrap.contains(e.relatedTarget)) return;
      setTimeout(function () {                           // after the blur above has padded the box
        if (wrap.contains(document.activeElement)) return;
        if (!boxes[0].value && !boxes[1].value && !boxes[2].value) { hideErr(); return; }
        commit(true);
      }, 0);
    });

    // Continue asks this for the precise reason, rather than "please answer this"
    wrap.__ddWhy = function () {
      var r = read(true);
      if (r.err) (boxes["ymd".indexOf(r.part)] || boxes[0]).setAttribute("aria-invalid", "true");
      return r.err || "";
    };
    return wrap;
  }

  /* ---- brand icons ------------------------------------------------------------
     The result page (template-src/page.html) carries a sprite of 24x24 line
     icons — fill:none, currentColor, round caps — and the design system says the
     two halves of the journey should feel like one surface. So the questionnaire
     draws from the same language rather than the old Shopify GIFs, which were
     raster, off-brand, one network request each, and left a hole in the badge
     when the CDN was slow. Six are the result page's own; the rest are new, in
     the same hand. Ids are namespaced so both sprites can coexist on one page. */
  var ICON_SPRITE =
    '<g id="dd-i-drop"><path d="M12 3.2 6.8 9.6a6.6 6.6 0 1 0 10.4 0Z"/></g>' +
    '<g id="dd-i-spark"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"/></g>' +
    '<g id="dd-i-pore"><circle cx="8" cy="8" r="3"/><circle cx="16.5" cy="14" r="4"/><circle cx="7" cy="16.5" r="2"/></g>' +
    '<g id="dd-i-leaf"><path d="M20 4c0 8.3-4.2 13-11 13H5.5C5.5 9.6 10.8 4 20 4Z"/><path d="M15 9 5 20"/></g>' +
    '<g id="dd-i-calm"><path d="M4 9.5c2.7-2 5.3-2 8 0s5.3 2 8 0"/><path d="M4 15c2.7-2 5.3-2 8 0s5.3 2 8 0"/></g>' +
    '<g id="dd-i-sun"><circle cx="12" cy="12" r="4"/><path d="M12 2v2.4M12 19.6V22M2 12h2.4M19.6 12H22M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M19.1 4.9l-1.7 1.7M6.6 17.4l-1.7 1.7"/></g>' +
    '<g id="dd-i-person"><circle cx="12" cy="8" r="3.6"/><path d="M4.8 20.2c0-3.7 3.2-6.2 7.2-6.2s7.2 2.5 7.2 6.2"/></g>' +
    '<g id="dd-i-family"><circle cx="9" cy="8.4" r="3"/><path d="M3.4 19.6c0-3.2 2.5-5.4 5.6-5.4s5.6 2.2 5.6 5.4"/><path d="M16.4 6.6a3 3 0 0 1 0 6"/><path d="M17.2 14.4c2.1.7 3.4 2.6 3.4 5.2"/></g>' +
    '<g id="dd-i-report"><rect x="5" y="3.5" width="14" height="17" rx="2.6"/><path d="M8.6 9.4h6.8M8.6 12.9h6.8M8.6 16.4h4"/></g>' +
    /* leaning, with a lick — a symmetric flame is just the droplet again at 14px */
    '<g id="dd-i-flame"><path d="M12.4 20.8a5.6 5.6 0 0 0 5.6-5.6c0-2.7-1.7-4.5-3.1-6.3-1.3-1.7-1.9-3.6-1.7-6.1-2.9 1.6-6.4 5.2-6.4 10.2a5.6 5.6 0 0 0 5.6 5.8Z"/><path d="M12.4 20.8a2.5 2.5 0 0 0 2.5-2.5c0-1.6-1.3-2.4-1.7-3.6-.8 1-2.5 1.9-2.5 3.6a2.5 2.5 0 0 0 1.7 2.5Z"/></g>' +
    '<g id="dd-i-pulse"><path d="M2.8 12.2h4L9.4 6l4.2 12 2.6-5.8h5"/></g>' +
    '<g id="dd-i-tone"><circle cx="9.4" cy="12" r="5.9"/><circle cx="14.6" cy="12" r="5.9"/></g>' +
    '<g id="dd-i-trend"><path d="M3.4 16.6 9 11l3.5 3.5L20.4 6.8"/><path d="M15.6 6.8h4.8v4.8"/></g>' +
    /* a lock, not three bare lines: strands plus the sweep that closes them */
    '<g id="dd-i-hair"><path d="M6.9 4.2c-1.8 3.7-2.2 8.4-1.1 12.2"/><path d="M12 3.5c-1.4 4-1.6 9.2-.6 13.2"/><path d="M17.1 4.2c1.8 3.7 2.2 8.4 1.1 12.2"/><path d="M5.6 16.2c1.7 2.6 4 3.9 6.4 3.9s4.7-1.3 6.4-3.9"/></g>' +
    '<g id="dd-i-eye"><path d="M2.6 12S6.2 5.6 12 5.6 21.4 12 21.4 12 17.8 18.4 12 18.4 2.6 12 2.6 12Z"/><circle cx="12" cy="12" r="2.7"/></g>';

  /* Which icon each section wears. Keyed on the eyebrow exactly as the spec
     spells it, lower-cased — the export is inconsistent about capitals
     ("SKIN HYDRATION" beside "Family History"). A section with no entry gets a
     badge with no icon rather than a wrong one; test/form.test.mjs fails if the
     spec grows a category that is not listed here. */
  var CATEGORY_ICON = {
    "demographics": "dd-i-person",
    "dermal diagnosis™": "dd-i-spark",
    "family history": "dd-i-family",
    "inflammation dx": "dd-i-report",
    "inflammatory conditions": "dd-i-flame",
    "medical background": "dd-i-pulse",
    "oxidative stress": "dd-i-leaf",
    "skin hydration": "dd-i-drop",
    "skin pigmentation": "dd-i-tone",
    "skin texture": "dd-i-pore",
    "sensitivity and inflammation": "dd-i-calm",
    "track and monitor your progress": "dd-i-trend",
    "when not exposed to the sun": "dd-i-sun",
    "with no artificial colorant": "dd-i-hair",
    "with no contact lenses": "dd-i-eye"
  };
  function iconFor(eyebrow) {
    return CATEGORY_ICON[String(eyebrow || "").trim().toLowerCase()] || null;
  }
  /* One hidden sprite for the document, added the first time a badge needs it. */
  function ensureIconSprite() {
    if (document.getElementById("dd-icons")) return;
    var host = document.createElement("div");
    host.id = "dd-icons";
    host.setAttribute("aria-hidden", "true");
    host.style.cssText = "position:absolute;width:0;height:0;overflow:hidden";
    host.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg"><defs>' + ICON_SPRITE + "</defs></svg>";
    document.body.appendChild(host);
  }

  /* Display blocks carry recovered STRUCTURE (eyebrow / heading / body), not the
     single run-on string the old spec had — which is why headings used to read
     "Demographics MY PERSONAL DETAILS We require this information" and why the
     conditional warnings rendered as enormous <h2>s. `notice: true` marks the
     ones Jotform colours as warnings (the age verifications). Body copy is
     plain text in the spec and goes in via textContent — no markup is ever
     injected from the export. */
  function renderDisplay(item) {
    if (item.absorbed) return null;   // its lines now live on the tiles
    var isCaption = !item.heading && !item.eyebrow && (item.body || []).length && !item.notice;
    /* Two different things wear this class, and they used to look identical.
       A block WITH an eyebrow is the category header — the same "Inflammatory
       Conditions / My Skin's Acne Risks" that repeats on every page of the
       section. A block WITHOUT one is a single question's own heading. Rendered
       at the same weight, a sub-heading shouted as loudly as the category above
       it, which is what made the rosacea page read as two competing titles. */
    var isSub = !item.eyebrow && !item.notice && !isCaption && !!item.heading;
    var box = el("div", "dd-prompt dd-gated" + (item.notice ? " dd-notice" : "")
                 + (isCaption ? " dd-caption" : "") + (isSub ? " dd-prompt--sub" : ""));
    /* The category is repetition, not news — by the fourth page of a section the
       reader has it. It goes in as a small badge carrying the per-category icon
       that the spec has always held and nothing ever drew, so the page's own
       question is what leads. */
    if (item.eyebrow) {
      var badge = el("p", "dd-badge");
      /* `item.icon` still holds the old Shopify GIF URL for most sections. It is
         deliberately ignored: the brand set below is drawn, not fetched. */
      var iconId = iconFor(item.eyebrow);
      if (iconId) {
        ensureIconSprite();
        var ic = el("span", "dd-badge__icon");
        ic.setAttribute("aria-hidden", "true");
        ic.innerHTML = '<svg viewBox="0 0 24 24"><use href="#' + iconId + '"/></svg>';
        badge.appendChild(ic);
      }
      badge.appendChild(el("span", "dd-badge__cat", item.eyebrow));
      /* BOTH lines are the category — "Oxidative Stress" and "My Skin and
         Lifestyle" name the same section, and both repeated at full size on
         every page of it. The pair goes into the badge together so the
         question's own heading is the largest thing on the card. */
      if (item.heading) {
        badge.appendChild(el("span", "dd-badge__sep", "\u00b7"));
        badge.appendChild(el("span", "dd-badge__name", String(item.heading).trim()));
      }
      box.appendChild(badge);
    }
    /* Only fall back to the old flat `label` when there is no structured body to
       show. Seven blocks are pure body copy — instructions and captions like
       "For the below question, consider the sun…" — and no heading is recovered
       for them. Falling back printed the sentence twice: once as a giant <h2>
       from the label, once as the paragraph it actually is. */
    var body = item.body || [];
    // the category header's heading is in the badge above; don't print it twice
    var head = item.eyebrow ? "" : (item.heading || (body.length ? "" : (item.label || "")));
    if (head) box.appendChild(el("h2", null, String(head).trim()));
    if (body.length) {
      var list = null;
      for (var i = 0; i < body.length; i++) {
        if (body[i].type === "li") {
          if (!list) { list = el("ol", "dd-list"); box.appendChild(list); }
          list.appendChild(el("li", null, body[i].text));
        } else {
          list = null;
          box.appendChild(el("p", null, body[i].text));
        }
      }
    }
    return box;
  }

  /* A value the ENGINE derives rather than the respondent types — currently just
     412 "My age", read from 425. Read-only by construction: question 686 asks
     you to confirm the age is right, so it must not be possible to type an age
     that disagrees with the birth date above it. */
  function renderComputed(item) {
    var wrap = el("div", "dd-field dd-gated");
    /* No label is a choice (the account email on the last page reads from the
       sentence above it) — no empty caption, but still a name for screen readers. */
    if (item.label) wrap.appendChild(el("div", "dd-q", item.label));
    var out = el("div", "dd-readout");
    if (!item.label) out.setAttribute("aria-label", item.name || "value");
    out.setAttribute("data-computed", item.source || item.qid);
    if (item.suffix) out.setAttribute("data-suffix", item.suffix);
    wrap.appendChild(out);
    return wrap;
  }

  /* Fill every readout on screen. Age is derived locally as well as by the
     server so the number appears the moment a birth date is entered, using the
     engine's own arithmetic so the two can never disagree. */
  function paintComputedReadouts() {
    var nodes = document.querySelectorAll("[data-computed]");
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i], src = n.getAttribute("data-computed"), v = computed[src];
      /* A seeded value (the signed-in customer's address) is an ANSWER before it
         is ever echoed back by /evaluate, so read both. */
      if (v == null || v === "") v = answers[src];
      var suf = n.getAttribute("data-suffix");
      /* The numeric guard belongs to the age readout, which must never print a
         bare " years". A readout with no unit — the registered email address —
         is text, and blanking it was leaving the customer looking at "Enter your
         date of birth" on the last page of the form. */
      if (suf && !hasNumber(v)) v = "";
      var txt = v == null ? "" : String(v);
      if (suf && txt && txt.toLowerCase().indexOf(suf.toLowerCase()) === -1) txt += " " + suf;
      n.textContent = txt || (suf ? "Enter your date of birth" : "\u2014");
      n.classList.toggle("is-empty", !txt);
    }
  }

  function renderItem(item) {
    if (item.engineOwned) return null;   // a collector the engine fills, not a question
    if (item.kind === "display") return renderDisplay(item);
    switch (item.type) {
      case "widget":   return renderWidget(item);
      case "radio":    return renderChoice(item, false);
      case "checkbox": return renderChoice(item, true);
      case "computed": return renderComputed(item);
      case "datetime": return renderDate(item);
      case "email":    return renderText(item, "email");
      case "number":   return renderText(item, "number");
      case "textbox":  case "text": return renderText(item, "text");
      default:         return null;
    }
  }

  // ---- server-driven navigation ----------------------------------------------
  function fallbackPages() { return spec.pages.map(function (p) { return p.jfPage; }); }
  /* ---- speed: never ask the server a question it has just answered ------------
     Measured 2026-10-04 (Trinette: "sometimes Next takes a while"): /evaluate is
     ~30 ms warm from Johannesburg and the engine ~3 ms, so the server was not
     the wait. Three things were:
       1. picture cards fetched their images only after Next — ~0.5 s each the
          first time from Shopify's CDN (4 ms once cached). preloadAhead() now
          fetches the next pages' pictures while the current card is answered;
       2. every Next waited for a round-trip even when the background refresh
          (refreshComputed, after every answer) had already returned the answer
          for exactly these answers. lastEval remembers it, keyed by
          answersVersion, so Next is instant unless something changed;
       3. a smooth scroll to the top delayed the new card by up to half a second.
     If Next does have to wait, the button says so after 150 ms. */
  var answersVersion = 0;
  var lastEval = null;          // { v: answersVersion it answered, pages: [...] }
  function freshPages() {
    if (lastEval && lastEval.v === answersVersion && !computePending) return Promise.resolve(lastEval.pages);
    return evaluatePages();
  }

  function evaluatePages() {
    var sentV = answersVersion;
    return fetch(EVALUATE_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answers: answers }) })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (j && j.ok && j.computed) {
          computed = j.computed;
          if (!hasNumber(computed[AGE_QID])) { delete computed[AGE_QID]; delete computed[AGE_SHOWN_QID]; }
          refreshLocalAge();
        }
        if (j && j.ok && Array.isArray(j.pages) && j.pages.length) {
          if (sentV === answersVersion) lastEval = { v: sentV, pages: j.pages };
          return j.pages;
        }
        return fallbackPages();
      })
      .catch(function () { return fallbackPages(); });
  }

  /* Required-answer validation. There was none: you could click Continue through
     all 38 pages without answering anything, and the engine would then score
     whatever its defaults happened to be — which for a skipped birth date meant
     a confidently wrong age. Only VISIBLE fields are enforced, so a question
     hidden by its gating rule never blocks you. */
  function answered(item) {
    var v = answers[item.qid];
    return v != null && String(v).trim() !== "";
  }
  function clearError(qid) {
    for (var i = 0; i < rendered.length; i++) {
      if (rendered[i].item.qid === qid) {
        rendered[i].el.classList.remove("has-err");
        var e = rendered[i].el.querySelector(".dd-fielderr");
        if (e) e.parentNode.removeChild(e);
      }
    }
  }
  function validatePage() {
    /* Judge on RULE visibility, not on what is revealed so far — otherwise
       one-at-a-time would hide the unanswered questions from the check and
       Continue would sail past them. Only the first outstanding question is
       flagged: with progressive reveal it is the one on screen, and a wall of
       errors on questions nobody has been shown yet helps no one. */
    var first = null;
    for (var i = 0; i < rendered.length; i++) {
      var r = rendered[i];
      if (r.item.kind !== "input" || !r.item.required) continue;
      if (!visible(r.item)) continue;                     // gated away: not required
      r.el.classList.remove("has-err");
      var prev = r.el.querySelector(".dd-fielderr");
      if (prev) prev.parentNode.removeChild(prev);
      if (!answered(r.item) && !first) first = r;
    }
    /* "Terms and Conditions" offers two answers, and only one of them is
       acceptance — the other opens the terms to read. A non-empty answer is not
       the same as agreement, so the generic required check would let someone
       through on "View the terms and conditions". */
    if (!first) {
      for (var c = 0; c < rendered.length; c++) {
        var r2 = rendered[c];
        if (r2.item.qid !== TERMS_QID || r2.el.hidden) continue;
        if (String(answers[TERMS_QID] || "").toLowerCase().indexOf("agree") !== -1) continue;
        r2.el.classList.add("has-err");
        r2.el.appendChild(el("p", "dd-fielderr", "Please accept the terms and conditions to continue"));
        if (r2.el.scrollIntoView) r2.el.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
      }
      return true;
    }
    first.el.hidden = false;                              // make sure it is on screen
    first.el.classList.add("has-err");
    /* A field that knows WHY it is unanswered says so — a half-typed birth date
       is "Enter the day", not "Please answer this". */
    var why = (typeof first.el.__ddWhy === "function" && first.el.__ddWhy()) || "Please answer this to continue";
    first.el.appendChild(el("p", "dd-fielderr", why));
    if (first.el.scrollIntoView) first.el.scrollIntoView({ behavior: "smooth", block: "center" });
    return false;
  }

  /* A page whose every question is gated off is not a page. It is a heading, a
     line of instructions, and nothing to answer — which is exactly what
     "My Skin's Dermatitis Risks" becomes for someone who reports ordinary dry
     skin patches: neither the seborrheic areas question nor the eczema one
     applies to xerosis, and correctly so. Jotform skipped such a page silently;
     we were rendering it. Page visibility is decided by the server (nav-spec),
     which reasons about pages, not fields, so this can only be judged here,
     where the field gates are evaluated.

     The last page is never skipped: it is where the form is submitted. */
  function pageHasQuestion(jf) {
    var p = pageByJf[jf];
    if (!p) return true;                 // unknown page — never skip in silence
    for (var i = 0; i < p.items.length; i++) {
      if (p.items[i].kind !== "input") continue;
      if (visible(p.items[i])) return true;
    }
    return false;
  }
  /* The pages actually worth counting, so "step 6 of 24" does not jump a number
     when one is skipped. It is recomputed on every render, so it moves as
     answers open and close later pages — which the server-driven total already
     did before any of this. */
  function countablePages(vis) {
    return vis.filter(function (jf, i) { return i === vis.length - 1 || jf === currentJf || pageHasQuestion(jf); });
  }

  /* ---- cards within a page ----------------------------------------------------
     A Jotform page can hold several unrelated questions, each with its own
     heading, and stacking them on one card made a wall to scroll: the acne page
     asked where the breakouts are AND which part of the face they are on, one
     under the other. So a page is now dealt out as CARDS.

     This is presentation only. Page navigation, page numbering, every gate and
     the whole of the scoring still belong to the server, which never learns that
     a page was shown as two cards — nothing here can change what is asked or
     what it scores, only how much of it is on screen at once.

     A card starts at a display block with a heading of its OWN. An eyebrow marks
     the category header ("Inflammatory Conditions"), which is not a question and
     is carried onto every card of the page so the context never disappears. */
  function splitCards(items) {
    var heads = [], cards = [], cur = null;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!cur && it.kind === "display" && it.eyebrow) { heads.push(it); continue; }
      /* cardBreak (from the spec): a question that depends on a select-all answer
         above it gets its own card, so it is decided once, on Next, instead of
         appearing and vanishing while the boxes are being ticked. */
      var startsCard = (it.kind === "display" && !!it.heading && !it.eyebrow && !it.notice) || !!it.cardBreak;
      if (startsCard || !cur) { cur = []; cards.push(cur); }
      cur.push(it);
    }
    if (!cards.length) cards.push([]);
    return cards.map(function (c) { return heads.concat(c); });
  }
  function cardHasQuestion(items) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].kind !== "input") continue;
      if (visible(items[i])) return true;
    }
    return false;
  }
  /* The cards on a page worth showing. A card whose every question is gated away
     is skipped for the same reason a whole page is. If that leaves nothing at
     all, the page is handed back whole rather than vanishing — a respondent must
     never be shown a card with nothing on it, but nor may a page silently
     disappear because this got the split wrong. */
  function cardsFor(jf) {
    var p = pageByJf[jf];
    if (!p) return [[]];
    var all = splitCards(p.items);
    var live = all.filter(cardHasQuestion);
    return live.length ? live : all;
  }
  /* Steps count cards, not pages, or the counter would sit still while you
     answered your way through three of them. Other pages are measured with the
     same gating this page uses — `visible()` reads the global answer map, so it
     is as valid for a page not on screen as for the one that is. */
  function stepCount(vis) {
    var counted = countablePages(vis), total = 0, before = 0;
    for (var i = 0; i < counted.length; i++) {
      var n = cardsFor(counted[i]).length;
      if (counted[i] === currentJf) before = total;
      total += n;
    }
    return { total: Math.max(total, 1), before: before };
  }

  var advancing = false;   // Next now waits on the server; a second click must not skip a card
  function goNext(btn) {
    if (advancing || !validatePage()) return;
    advancing = true;
    var busyT = setTimeout(function () { if (btn) { btn.classList.add("is-busy"); btn.setAttribute("aria-busy", "true"); } }, 150);
    function done() { clearTimeout(busyT); advancing = false; if (btn) { btn.classList.remove("is-busy"); btn.removeAttribute("aria-busy"); } }
    /* Ask the server first, then decide. Whether the next card has a question
       on it can depend on a value only the server computes (the comedone-count
       card waits on 1791, worked out from the breakout types just ticked); with
       a stale value the card would be skipped as empty. */
    freshPages().then(function (vis) {
      done();
      /* Another card on this page comes before the next page does. */
      if (currentCard < cardsFor(currentJf).length - 1) {
        render(currentJf, vis, currentCard + 1);
        return;
      }
      var next = null;
      for (var i = 0; i < vis.length; i++) {
        if (vis[i] <= currentJf) continue;
        if (i < vis.length - 1 && !pageHasQuestion(vis[i])) continue;
        next = vis[i]; break;
      }
      if (next != null) render(next, vis, 0);
      else { pruneForSubmit(vis); submit(btn); }
    }, done);
  }
  function goBack() {
    if (currentCard > 0) {
      render(currentJf, visiblePages, currentCard - 1);
      return;
    }
    freshPages().then(function (vis) {
      var prev = null;
      for (var i = vis.length - 1; i >= 0; i--) {
        if (vis[i] >= currentJf) continue;
        if (!pageHasQuestion(vis[i])) continue;
        prev = vis[i]; break;
      }
      // land on the LAST card of the previous page — the one just left
      if (prev != null) render(prev, vis, cardsFor(prev).length - 1);
    });
  }

  // ---- render one page -------------------------------------------------------
  function render(jf, vis, cardIdx) {
    currentJf = jf; visiblePages = vis;
    rendered = []; firstPaint = true;
    var mount = document.getElementById("dd-app");
    mount.innerHTML = "";
    var page = pageByJf[jf];
    if (!page) { mount.appendChild(el("div", "dd-wrap")).appendChild(el("div", "dd-card")).appendChild(el("p", "dd-err", "No spec for page " + jf)); return; }
    var cards = cardsFor(jf);
    currentCard = Math.min(Math.max(cardIdx || 0, 0), cards.length - 1);
    var items = cards[currentCard];
    var step = stepCount(vis);
    var pos = step.before + currentCard, total = step.total;
    if (pos < 0 || pos >= total) pos = Math.min(Math.max(pos, 0), total - 1);
    /* The last card of the last page is where the form is submitted — not the
       first card of it. */
    var isFinal = jf === vis[vis.length - 1] && currentCard === cards.length - 1;

    var wrap = el("div", "dd-wrap");

    // progress bar
    var prog = el("div", "dd-progress"); var bar = el("i");
    bar.style.width = (((pos + 1) / total) * 100) + "%"; prog.appendChild(bar); wrap.appendChild(prog);

    // eyebrow + step
    var eyebrow = el("p", "dd-eyebrow");
    eyebrow.appendChild(document.createTextNode("Dermal Diagnosis  "));
    eyebrow.appendChild(el("span", "dd-step", "step " + (pos + 1) + " of " + total));
    wrap.appendChild(eyebrow);

    var card = el("div", "dd-card");

    // page title only when the page leads with no display block (avoids dupes)
    var hasDisplay = items.some(function (i) { return i.kind === "display"; });
    if (!hasDisplay && page.title) {
      var pt = el("div", "dd-prompt dd-pagetitle"); pt.appendChild(el("h2", null, page.title)); card.appendChild(pt);
    }

    items.forEach(function (item) {
      var node = renderItem(item);
      if (!node) return;
      card.appendChild(node);
      rendered.push({ item: item, el: node });
    });

    var foot = el("div", "dd-foot");
    if (pos > 0) {
      var back = el("button", "dd-btn dd-btn-ghost", "Back"); back.type = "button";
      back.addEventListener("click", goBack); foot.appendChild(back);
    }
    var next = el("button", "dd-btn", isFinal ? "See my result" : "Continue"); next.type = "button";
    if (pos > 0) next.style.marginLeft = "auto";
    next.addEventListener("click", function () { goNext(next); });
    foot.appendChild(next);
    card.appendChild(foot);

    // dev affordance: fill in a sample respondent's answers so you can jump
    // ahead and exercise /score without typing every field. Rendered only on a
    // local host — never on the storefront.
    if (IS_DEV) {
      var dev = el("button", "dd-note dd-devlink", "Dev: autofill sample respondent"); dev.type = "button";
      dev.addEventListener("click", devAutofill); card.appendChild(dev);
    }

    wrap.appendChild(card);
    mount.appendChild(wrap);
    applyGating();
    paintComputedReadouts();
    window.scrollTo(0, 0);       // instant: the new card is the answer to the click
    preloadAhead(jf, vis);
  }

  /* Fetch the pictures the respondent is about to see — the rest of this page's
     cards and the next two pages on her path — while she answers this one.
     Only pictures whose question would show on the answers so far (plus picker
     families, whose variant is settled on screen). A few small PNGs, once each. */
  var preloaded = {};
  function preloadAhead(jf, vis) {
    var run = function () {
      var at = (vis || []).indexOf(jf), pages = [jf];
      for (var k = at + 1; k < (vis || []).length && pages.length < 3; k++) pages.push(vis[k]);
      pages.forEach(function (pj) {
        var pg = pageByJf[pj];
        (pg && pg.items || []).forEach(function (it) {
          if (!it.widgetItems || Array.isArray(it.widgetItems)) return;
          if (!it.family && !visible(it)) return;
          Object.keys(it.widgetItems).forEach(function (key) {
            var url = it.widgetItems[key];
            if (!url || preloaded[url]) return;
            var img = new Image(); img.decoding = "async"; img.src = url;
            preloaded[url] = img;
          });
        });
      });
    };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1200 });
    else setTimeout(run, 300);
  }

  /* Build the diagnosis-page URL.

     This used to carry the respondent's whole curated profile as query
     parameters — first name, surname, PREGNANCY STATUS, skin colour and all
     eight clinical percentages, written into the address bar. Shopify's
     analytics read that, it lands in the browser history of a possibly shared
     device, and it leaks in the Referer header of every external request the
     results page makes. For a medical assessment that is not acceptable.

     Now the link carries ONE opaque token and the page fetches the rest.

     If the token is missing — D1 was briefly unavailable, so the submission was
     scored but not stored — we fall back to the RESULT CODE ALONE. Deliberately
     not the old parameter soup: a respondent seeing a generic version of the
     right diagnosis is a far better failure than quietly publishing their
     pregnancy status to the address bar. */
  function resultsHref(data) {
    var sep = RESULTS_URL.indexOf("?") >= 0 ? "&" : "?";
    var token = data && data.token;
    if (token) return RESULTS_URL + sep + "k=" + encodeURIComponent(token);
    var code = (data && data.result_code) || ((data && data.profile) || {}).result_code || "";
    return code ? RESULTS_URL + sep + "result_code=" + encodeURIComponent(code) : RESULTS_URL;
  }

  // ---- submit + result -------------------------------------------------------
  function showResult(data, payload, isErr) {
    // SUCCESS → hand off to the diagnosis page carrying the profile as params.
    // In production we auto-redirect. In local dev we DON'T (the diagnosis page
    // is served by a different dev server), so we show the destination link
    // instead — keeping the local click-through round-trip usable.
    if (!isErr && !IS_DEV) { location.assign(resultsHref(data)); return; }

    var mount = document.getElementById("dd-app");
    mount.innerHTML = "";
    var wrap = el("div", "dd-wrap");
    var card = el("div", "dd-card");
    if (isErr) {
      card.appendChild(el("p", "dd-err", "Could not score: " + (data && data.error || data)));
    } else {
      card.appendChild(el("p", "dd-eyebrow", "Your result code"));
      card.appendChild(el("div", "dd-code", data.result_code || "(none)"));
      if (data.result_word) card.appendChild(el("div", "dd-word", data.result_word));
      if (data.final_url) { var a = el("a", null, data.final_url); a.href = data.final_url; a.target = "_blank"; card.appendChild(el("div", null)).appendChild(a); }
      // dev-only: show exactly where production would auto-redirect, clickable.
      if (IS_DEV) { var rh = resultsHref(data); var rl = el("a", null, "→ " + rh); rl.href = rh; card.appendChild(el("p", "dd-eyebrow", "Prod auto-redirects to")); card.appendChild(el("div", null)).appendChild(rl); }
    }
    // dev-only: echo the exact request payload so you can eyeball what was sent.
    // Never on the storefront — it would dump the respondent's answers into the page.
    if (IS_DEV) card.appendChild(el("div", "dd-pay", "POST " + SCORE_URL + "\n" + JSON.stringify(payload, null, 2)));
    var foot = el("div", "dd-foot");
    var again = el("button", "dd-btn dd-btn-ghost", "Start over"); again.type = "button";
    again.addEventListener("click", function () { answers = {}; boot(spec); });
    foot.appendChild(again);
    card.appendChild(foot);
    wrap.appendChild(card);
    mount.appendChild(wrap);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function submit(btn) {
    var payload = { answers: answers };
    btn.disabled = true; btn.textContent = "Scoring…";
    fetch(SCORE_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) { showResult(res.j, payload, !res.ok || res.j.ok === false); })
      .catch(function (e) { showResult({ error: String(e) }, payload, true); });
  }

  // ---- dev autofill ----------------------------------------------------------
  function devAutofill() {
    fetch(BASELINE_URL).then(function (r) { return r.ok ? r.json() : null; }).then(function (b) {
      if (!b || !b.answers) return;
      Object.keys(b.answers).forEach(function (k) { answers[k] = b.answers[k]; });
      answersVersion++;
      evaluatePages().then(function (vis) {
        render(vis.indexOf(currentJf) !== -1 ? currentJf : vis[0], vis);
      });
    }).catch(function () {});
  }

  // ---- boot ------------------------------------------------------------------
  /* The storefront knows whether a Shopify customer is signed in; the form does
     not. dd-questionnaire.liquid hands it over, and it lands in the two
     collectors the last page branches on: with a customer id we show the address
     on their account and ask whether to use it, without one we ask for an
     address. Seeded as ANSWERS, so they are also what gets submitted. */
  function seedCustomer() {
    var c = window.DD_CUSTOMER;
    if (!c || typeof c !== "object") return;
    if (c.id) answers["2161"] = String(c.id);
    if (c.email) answers["2162"] = String(c.email);
    if (!answers["4"] && c.first_name) answers["4"] = String(c.first_name);
    if (!answers["20"] && c.last_name) answers["20"] = String(c.last_name);
  }

  function boot(s) {
    try {
      var pc = document.createElement("link");
      pc.rel = "preconnect"; pc.href = "https://cdn.shopify.com";
      document.head.appendChild(pc);
    } catch (e) {}
    seedCustomer();
    spec = s;
    pageByJf = {};
    spec.pages.forEach(function (p) { pageByJf[p.jfPage] = p; });
    evaluatePages().then(function (vis) {
      if (!vis.length) vis = fallbackPages();
      render(vis[0], vis);
    });
  }

  fetch(SPEC_URL)
    .then(function (r) { return r.json(); })
    .then(boot)
    .catch(function (e) {
      document.getElementById("dd-app").innerHTML =
        '<div class="dd-wrap"><div class="dd-card"><p class="dd-err">Could not load form-spec.json: ' + e + "</p></div></div>";
    });
})();
